import React, { useState, useRef } from 'react';

const PaymentStep = ({ formData, calculatePrice, isAddingToCart, handleAddToCart, mapGenerated }) => {
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const starMapRef = useRef(null);

    // Function to generate and download the PDF
    const handleGeneratePDF = async () => {
        try {
            setIsGeneratingPDF(true);

            // Get the entire map container with decorations
            const mapContainer = document.querySelector('.poster-bg-container');
            if (!mapContainer) {
                console.error('No map container found');
                setIsGeneratingPDF(false);
                return;
            }

            // Use html2canvas to capture the entire map with decorations
            try {
                // Dynamic import html2canvas
                const html2canvasModule = await import('html2canvas');
                const html2canvas = html2canvasModule.default;

                const canvas = await html2canvas(mapContainer, {
                    backgroundColor: null,
                    scale: 2, // Higher resolution
                    logging: false,
                    useCORS: true,
                    allowTaint: true
                });

                // Get the data URL from the canvas
                const imageData = canvas.toDataURL('image/png');

                // Prepare the data to send to the server, including all form data
                const pdfData = {
                    imageData: imageData,
                    // Include all form data so the PDF can match the preview exactly
                    ...formData,
                    order_id: formData.order_id || Date.now().toString(),
                    // Add capture method to inform backend that image already includes captions
                    captureMethod: 'html2canvas'
                };

                // Send the data to the server for PDF generation
                const response = await fetch('/api/save-starmap', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(pdfData),
                });

                const result = await response.json();
                if (result.success) {
                    setPdfUrl(result.pdf_url);
                } else {
                    console.error('Error generating PDF:', result.message);
                    alert('Error generating PDF. Please try again.');
                }
            } catch (error) {
                console.error('Error capturing map with html2canvas:', error);
                fallbackCaptureMethod();
            }

            setIsGeneratingPDF(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setIsGeneratingPDF(false);
            alert('Error generating PDF. Please try again.');
        }
    };

    // Fallback capture method if html2canvas fails
    const fallbackCaptureMethod = () => {
        try {
            // Capture just the star map as a fallback
            const starMapCanvas = document.querySelector('#celestial-map-container canvas');
            if (!starMapCanvas) {
                console.error('No star map canvas found');
                return;
            }

            // Create an image with proper dimensions
            const captureWidth = starMapCanvas.width;
            const captureHeight = starMapCanvas.height;

            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = captureWidth;
            offscreenCanvas.height = captureHeight;

            const ctx = offscreenCanvas.getContext('2d');
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, captureWidth, captureHeight);
            ctx.drawImage(starMapCanvas, 0, 0, captureWidth, captureHeight);

            // Get the star map data URL
            const imageData = offscreenCanvas.toDataURL('image/png');

            // Prepare the data to send to the server, including all form data
            const pdfData = {
                imageData: imageData,
                // Include all form data so the PDF can match the preview exactly
                ...formData,
                order_id: formData.order_id || Date.now().toString(),
                // Specify fallback capture method
                captureMethod: 'canvas'
            };

            // Send the data to the server for PDF generation
            fetch('/api/save-starmap', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(pdfData),
            })
                .then(response => response.json())
                .then(result => {
                    if (result.success) {
                        setPdfUrl(result.pdf_url);
                    } else {
                        console.error('Error generating PDF:', result.message);
                        alert('Error generating PDF. Please try again.');
                    }
                });
        } catch (error) {
            console.error('Fallback capture failed:', error);
            alert('Error generating PDF. Please try again.');
        }
    }

    // Background pricing helper
    const getBackgroundPrice = () => {
        const backgroundPrices = {
            'basic-light': 5.00,
            'basic-dark': 5.00,
            'sunset-pink': 8.80,
            'sunset-orange': 8.80,
            'pastel-pink': 6.50,
            'pastel-blue': 6.50,
            'pastel-white': 6.50,
            'neon-purple': 9.90,
            'neon-blue': 9.90,
            'natural-gold': 7.20,
            'natural-brown': 7.20,
            'natural-beige': 7.20,
            'golden-dark': 11.00,
            'golden-black': 11.00,
            'golden-gold': 11.00
        };

        return formData.background ? backgroundPrices[formData.background] || 0 : 0;
    };

    // Get background name for display
    const getBackgroundName = () => {
        const types = {
            'basic-light': 'Basic Harmony (Light)',
            'basic-dark': 'Basic Harmony (Dark)',
            'sunset-pink': 'Sunset Vibe (Pink)',
            'sunset-orange': 'Sunset Vibe (Orange)',
            'pastel-pink': 'Pastel Whispers (Pink)',
            'pastel-blue': 'Pastel Whispers (Blue)',
            'pastel-white': 'Pastel Whispers (White)',
            'neon-purple': 'Neon Pop (Purple)',
            'neon-blue': 'Neon Pop (Blue)',
            'natural-gold': 'Natural Soul (Gold)',
            'natural-brown': 'Natural Soul (Brown)',
            'natural-beige': 'Natural Soul (Beige)',
            'golden-dark': 'Golden Elegance (Dark)',
            'golden-black': 'Golden Elegance (Black)',
            'golden-gold': 'Golden Elegance (Gold)'
        };

        return formData.background ? types[formData.background] : 'None';
    };

    // Celestial element helper
    const getCelestialElements = () => {
        const elements = [
            { id: 'showStars', name: 'Stars', price: 4.00 },
            { id: 'showConstellations', name: 'Constellation lines', price: 5.00 },
            { id: 'showConstellationNames', name: 'Constellation names', price: 5.00 },
            { id: 'showGrid', name: 'Coordination Grid', price: 8.00 },
            { id: 'showDeepSkyObjects', name: 'Deep Sky Objects', price: 13.00 },
            { id: 'showEcliptic', name: 'Ecliptic line', price: 17.50 }
        ];

        return elements.filter(element => formData[element.id]);
    };

    return (
        <div className="space-y-6">

            <div className="-mt-4">
                <h3 className="text-[31px] font-light mb-4">Review Your Order</h3>
                <p className="mb-8 text-gray-300 text-md">
                    If you'd like to make any changes, please go back to the previous tabs.
                </p>
            </div>

            <div className="border-t border-dotted border-[#A0A0A0] mt-10 mb-8"></div>

            <div className="bg-black/30 rounded-lg !text-md">
                <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                        <span className="text-white">Star Map</span>
                        <span>€29.99</span>
                    </div>

                    {formData.background && (
                        <div className="flex justify-between">
                            <span className="text-white">Background ({getBackgroundName()})</span>
                            <span>+€{getBackgroundPrice().toFixed(2)}</span>
                        </div>
                    )}

                    {formData.size !== '8×10 in / 20×25 cm' && (
                        <div className="flex justify-between">
                            <span className="text-white">Size ({formData.size})</span>
                            <span>
                                {formData.size === '12×16 in / 30×40 cm' ? '+€10.00' : '+€20.00'}
                            </span>
                        </div>
                    )}

                    {formData.frame !== 'none' && (
                        <div className="flex justify-between">
                            <span className="text-white">Frame ({formData.frame})</span>
                            <span>
                                {formData.frame === 'black' ? '+€20.00' :
                                    formData.frame === 'wood' ? '+€15.00' : '+€25.00'}
                            </span>
                        </div>
                    )}

                    {getCelestialElements().map(element => (
                        <div key={element.id} className="flex justify-between">
                            <span className="text-white">{element.name}</span>
                            <span>+€{element.price.toFixed(2)}</span>
                        </div>
                    ))}

                    <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

                    <div className="flex justify-between font-bold">
                        <span>Total</span>
                        <span className="text-accent">€{calculatePrice()}</span>
                    </div>

                    <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

                </div>

                <div className="text-md text-gray-400 mb-3">
                    <p className="mb-2">• Free shipping included</p>
                    <p className="mb-2">• Printed on premium archival paper</p>
                    <p className="mb-2">• Ships within 2-3 business days</p>
                </div>

                <div className="border-t border-dotted border-[#A0A0A0] !mt-6 !mb-6"></div>

            </div>

            <div className="bg-black/30 rounded-lg !mt-8 p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <button
                        onClick={handleAddToCart}
                        disabled={!mapGenerated || isAddingToCart}
                        className={`w-full md:w-[300px] mr-auto flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isAddingToCart ? 'bg-gray-600 cursor-not-allowed' : 'bg-accent text-primary hover:bg-accent/90'}`}
                    >
                        {isAddingToCart ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <span className="!text-[14px] md:!text-md !text-white">Add to Cart</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={handleGeneratePDF}
                        disabled={!mapGenerated || isGeneratingPDF}
                        className={`w-full md:w-[300px] mr-auto flex items-center justify-center py-3 px-6 rounded-[50px] font-semibold transition duration-300 ${!mapGenerated || isGeneratingPDF ? 'bg-gray-600 cursor-not-allowed' : 'bg-white text-primary hover:bg-white/90'}`}
                    >
                        {isGeneratingPDF ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Generating PDF...</span>
                            </>
                        ) : (
                            <>
                                <span className="!text-[14px] md:!text-md">Get Digital PDF</span>
                            </>
                        )}
                    </button>
                </div>

                {pdfUrl && (
                    <div className="mt-4 text-center">
                        <a
                            href={pdfUrl}
                            download={`starmap-${formData.caption || 'personalized'}.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition duration-300"
                        >
                            Download Your Star Map PDF
                        </a>
                        <p className="text-sm text-gray-400 mt-2">Your digital star map is ready! Click the button above to download.</p>
                        <p className="text-xs text-gray-500 mt-1">This is the exact same design you'll receive as a physical poster.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentStep; 